import _Object$assign from 'babel-runtime/core-js/object/assign';
import _JSON$stringify from 'babel-runtime/core-js/json/stringify';
import _Promise from 'babel-runtime/core-js/promise';
import _Map from 'babel-runtime/core-js/map';
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CredentialsProvider, makeCredentialsGetter } from '../credentials_provider';
import { StatusMessage } from '../status';
import { CANCELED, CancellationTokenSource, uncancelableToken } from './cancellation';
import { removeFromParent } from './dom';
import { parseArray, verifyObject, verifyString } from './json';
import { getRandomHexString } from './random';
import { Signal } from './signal';
export const AUTH_SERVER = 'https://accounts.google.com/o/oauth2/auth';
const AUTH_ORIGIN = 'https://accounts.google.com';
export function embedRelayFrame(proxyName, rpcToken) {
    let iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.id = proxyName;
    iframe.name = proxyName;
    const origin = location.origin;
    iframe.src = `https://accounts.google.com/o/oauth2/postmessageRelay?` + `parent=${encodeURIComponent(origin)}#rpctoken=${rpcToken}`;
    document.body.appendChild(iframe);
}
class PendingRequest {
    constructor() {
        this.finished = new Signal();
    }
}
class AuthHandler {
    constructor() {
        this.proxyName = `postmessageRelay${getRandomHexString()}`;
        this.rpcToken = `${getRandomHexString()}`;
        this.relayReadyService = `oauth2relayReady:${this.rpcToken}`;
        this.oauth2CallbackService = `oauth2callback:${this.rpcToken}`;
        this.pendingRequests = new _Map();
        embedRelayFrame(this.proxyName, this.rpcToken);
        this.relayReadyPromise = new _Promise(relayReadyPromiseResolve => {
            addEventListener('message', event => {
                if (event.origin !== AUTH_ORIGIN) {
                    return;
                }
                try {
                    let data = verifyObject(JSON.parse(event.data));
                    let service = verifyString(data['s']);
                    if (service === this.relayReadyService) {
                        relayReadyPromiseResolve();
                    }
                    if (service === this.oauth2CallbackService) {
                        let args = parseArray(data['a'], x => x);
                        let arg = verifyString(args[0]);
                        let origin = location.origin;
                        if (!arg.startsWith(origin + '#') && !arg.startsWith(origin + '?')) {
                            throw new Error(`oauth2callback: URL ${_JSON$stringify(arg)} ` + `does not match current origin ${origin}.`);
                        }
                        let hashPart = arg.substring(origin.length + 1);
                        let parts = hashPart.split('&');
                        let params = new _Map();
                        for (let part of parts) {
                            let match = part.match('^([a-z_]+)=(.*)$');
                            if (match === null) {
                                throw new Error(`oauth2callback: URL part ${_JSON$stringify(match)} ` + `does not match expected pattern.`);
                            }
                            params.set(match[1], match[2]);
                        }
                        let state = params.get('state');
                        if (state === undefined) {
                            throw new Error(`oauth2callback: State argument is missing.`);
                        }
                        let request = this.pendingRequests.get(state);
                        if (request === undefined) {
                            // Request may have been cancelled.
                            return;
                        }
                        let error = params.get('error');
                        if (error !== undefined) {
                            let errorSubtype = params.get('error_subtype');
                            let fullMessage = error;
                            if (errorSubtype !== undefined) {
                                fullMessage += ': ' + errorSubtype;
                            }
                            request.finished.dispatch(undefined, new Error(`Error obtaining Google OAuth2 token: ${fullMessage}`));
                            return;
                        }
                        let accessToken = params.get('access_token');
                        let tokenType = params.get('token_type');
                        let expiresIn = params.get('expires_in');
                        let scope = params.get('scope');
                        if (accessToken === undefined || tokenType === undefined || expiresIn === undefined || scope === undefined) {
                            throw new Error(`oauth2callback: URL lacks expected parameters.`);
                        }
                        request.finished.dispatch({
                            accessToken: accessToken,
                            tokenType: tokenType,
                            expiresIn: expiresIn,
                            scope: scope
                        });
                        return;
                    }
                } catch (parseError) {
                    throw new Error(`Invalid message received from ${AUTH_ORIGIN}: ${_JSON$stringify(event.data)}: ` + `${parseError.message}.`);
                }
            });
        });
    }
    addPendingRequest(state) {
        let request = new PendingRequest();
        this.pendingRequests.set(state, request);
        request.finished.add(() => {
            this.pendingRequests.delete(state);
        });
        return request;
    }
    makeAuthRequestUrl(options) {
        let url = `${AUTH_SERVER}?client_id=${encodeURIComponent(options.clientId)}`;
        url += `&redirect_uri=postmessage`;
        url += `&response_type=token`;
        var _options$origin = options.origin;
        let origin = _options$origin === undefined ? location.origin : _options$origin;

        url += `&origin=${encodeURIComponent(origin)}`;
        url += `&proxy=${this.proxyName}`;
        url += `&include_granted_scopes=true`;
        url += `&scope=${encodeURIComponent(options.scopes.join(' '))}`;
        if (options.state) {
            url += `&state=${options.state}`;
        }
        if (options.approvalPrompt) {
            url += `&approval_prompt=${encodeURIComponent(options.approvalPrompt)}`;
        }
        if (options.loginHint) {
            url += `&login_hint=${encodeURIComponent(options.loginHint)}`;
        }
        if (options.immediate) {
            url += `&immediate=true`;
        }
        if (options.authUser !== undefined) {
            url += `&authuser=${options.authUser}`;
        }
        return url;
    }
}
let authHandlerInstance;
function authHandler() {
    if (authHandlerInstance === undefined) {
        authHandlerInstance = new AuthHandler();
    }
    return authHandlerInstance;
}
/**
 * Obtain a Google OAuth2 authentication token.
 * @return A Promise that resolves to an authentication token.
 */
export function authenticateGoogleOAuth2(options, cancellationToken = uncancelableToken) {
    const state = getRandomHexString();
    const handler = authHandler();
    const url = handler.makeAuthRequestUrl({
        state,
        clientId: options.clientId,
        scopes: options.scopes,
        approvalPrompt: options.approvalPrompt,
        loginHint: options.loginHint,
        immediate: options.immediate,
        authUser: options.authUser
    });
    const request = handler.addPendingRequest(state);
    const promise = new _Promise((resolve, reject) => {
        request.finished.add((token, error) => {
            if (token !== undefined) {
                resolve(token);
            } else {
                reject(error);
            }
        });
    });
    request.finished.add(cancellationToken.add(() => {
        request.finished.dispatch(undefined, CANCELED);
    }));
    if (options.immediate) {
        // For immediate mode auth, we can wait until the relay is ready, since we aren't opening a new
        // window.
        handler.relayReadyPromise.then(() => {
            if (cancellationToken.isCanceled) {
                return;
            }
            const iframe = document.createElement('iframe');
            iframe.src = url;
            iframe.style.display = 'none';
            document.body.appendChild(iframe);
            request.finished.add(() => {
                removeFromParent(iframe);
            });
        });
    } else {
        if (!cancellationToken.isCanceled) {
            const newWindow = open(url);
            if (newWindow !== null) {
                request.finished.add(() => {
                    newWindow.close();
                });
            }
        }
    }
    return promise;
}
export class GoogleOAuth2CredentialsProvider extends CredentialsProvider {
    constructor(options) {
        super();
        this.options = options;
        this.get = makeCredentialsGetter(cancellationToken => {
            const options = this.options;

            const status = new StatusMessage( /*delay=*/true);
            let cancellationSource;
            return new _Promise((resolve, reject) => {
                const dispose = () => {
                    cancellationSource = undefined;
                    status.dispose();
                };
                cancellationToken.add(() => {
                    if (cancellationSource !== undefined) {
                        cancellationSource.cancel();
                        cancellationSource = undefined;
                        status.dispose();
                        reject(CANCELED);
                    }
                });
                function writeLoginStatus(msg = `${options.description} authorization required.`, linkMessage = 'Request authorization.') {
                    status.setText(msg + '  ');
                    let button = document.createElement('button');
                    button.textContent = linkMessage;
                    status.element.appendChild(button);
                    button.addEventListener('click', () => {
                        login( /*immediate=*/false);
                    });
                    status.setVisible(true);
                }
                function login(immediate) {
                    if (cancellationSource !== undefined) {
                        cancellationSource.cancel();
                    }
                    cancellationSource = new CancellationTokenSource();
                    writeLoginStatus(`Waiting for ${options.description} authorization...`, 'Retry');
                    authenticateGoogleOAuth2({
                        clientId: options.clientId,
                        scopes: options.scopes,
                        immediate: immediate,
                        authUser: 0
                    }, cancellationSource).then(token => {
                        if (cancellationSource !== undefined) {
                            dispose();
                            resolve(token);
                        }
                    }, reason => {
                        if (cancellationSource !== undefined) {
                            cancellationSource = undefined;
                            if (immediate) {
                                writeLoginStatus();
                            } else {
                                writeLoginStatus(`${options.description} authorization failed: ${reason}.`, 'Retry');
                            }
                        }
                    });
                }
                login( /*immediate=*/true);
            });
        });
    }
}
export function fetchWithGoogleCredentials(credentialsProvider, input, init = {}) {
    function start(credentials) {
        const token = credentials.credentials;
        const headers = new Headers(init.headers);
        headers.append('Authorization', `${token.tokenType} ${token.accessToken}`);
        return fetch(input, _Object$assign(_Object$assign({}, init), { mode: 'cors', headers })).then(response => {
            if (response.status === 401) {
                // 401: Authorization needed.  OAuth2 token may have expired.
                return credentialsProvider.get(credentials).then(start);
            }
            return response;
        });
    }
    const promise = credentialsProvider.get( /*invalidToken=*/undefined).then(start);
    const disposeRef = () => {
        credentialsProvider.dispose();
    };
    promise.then(disposeRef, disposeRef);
    return promise;
}
//# sourceMappingURL=google_oauth2.js.map